import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { Subscription } from 'rxjs';
import { environment } from '../../../../environments/environment';

const CHECK_INTERVAL_MS = 5 * 60 * 1000;
const SNOOZE_MS = 5 * 60 * 1000;
const UNSTAMPED_VERSION = '__BUILD_VERSION__';
const RELOAD_TARGET_KEY = 'app-reload-target';
/** Same test as safeAfterLoginUrl: /app, /app/..., /app?..., /app#... */
const APP_URL = /^\/app(?:[/?#]|$)/;

/**
 * Asks for a reload once a newer UI build is deployed. The deploy stamps the
 * commit SHA into the bundle and into /version.json; a tab still running an
 * older bundle (left open for days, like the one that overwrote inspection
 * 26197) gets a modal asking it to reload; "later" hides it for five minutes.
 * The login page has nothing to lose, so it gets no modal: the navigation
 * from it into the app loads the new build instead.
 */
@Injectable({
  providedIn: 'root'
})
export class AppVersionService implements OnDestroy {
  private intervalRef: ReturnType<typeof setInterval>;
  private modalRef: NzModalRef;
  private modalVersion: string;
  private checking = false;
  private snoozed = false;
  private snoozeRef: ReturnType<typeof setTimeout>;
  /** A newer build seen outside the app, waiting for the way in. */
  private pendingVersion: string;
  /** A full page load into the new build has started. */
  private handedOff = false;
  private navigationSub: Subscription;

  constructor(
    private modal: NzModalService,
    private zone: NgZone,
    private router: Router,
    private transloco: TranslocoService
  ) {}

  private get isStamped(): boolean {
    return (
      !!environment.buildVersion &&
      environment.buildVersion !== UNSTAMPED_VERSION
    );
  }

  /**
   * The address bar, not router.url: the router reports '/' until the first
   * navigation ends, and the first check usually finishes before that.
   */
  private currentPath(): string {
    return window.location.pathname;
  }

  private get inApp(): boolean {
    return APP_URL.test(this.currentPath());
  }

  start(): void {
    // Local and unstamped builds have nothing to compare against.
    if (!this.isStamped || this.intervalRef) {
      return;
    }

    this.navigationSub = this.router.events.subscribe((e) => {
      if (e instanceof NavigationStart) {
        this.onNavigationStart(e.url);
      } else if (e instanceof NavigationEnd) {
        this.onNavigationEnd(e.urlAfterRedirects);
      }
    });
    this.zone.runOutsideAngular(() => {
      this.intervalRef = setInterval(() => this.check(), CHECK_INTERVAL_MS);
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      window.addEventListener('online', this.onOnline);
    });
    this.check();
  }

  ngOnDestroy(): void {
    clearInterval(this.intervalRef);
    clearTimeout(this.snoozeRef);
    this.navigationSub?.unsubscribe();
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    window.removeEventListener('online', this.onOnline);
  }

  private onVisibilityChange = (): void => {
    if (document.visibilityState === 'visible') {
      this.check();
    }
  };

  private onOnline = (): void => {
    this.check();
  };

  private onNavigationStart(url: string): void {
    if (APP_URL.test(url)) {
      // From the login page into the app: nothing to lose yet, so load the
      // new build with the page instead of asking.
      if (this.pendingVersion && !this.inApp) {
        this.reload(this.pendingVersion, url);
      }
    } else if (this.modalRef) {
      // Leaving the app (an expired session): the login page gets no modal.
      this.pendingVersion = this.modalVersion;
      this.modalRef.destroy();
      this.modalRef = null;
    }
  }

  /** Reached the app some other way (a redirect from '/'): ask as usual. */
  private onNavigationEnd(url: string): void {
    if (this.pendingVersion && APP_URL.test(url)) {
      this.promptReload(this.pendingVersion);
    }
  }

  private async check(): Promise<void> {
    if (this.modalRef || this.snoozed || this.checking) {
      return;
    }

    this.checking = true;
    try {
      // fetch() bypasses the API interceptors; version.json lives on the UI host.
      const resp = await fetch(`/version.json?t=${Date.now()}`, {
        cache: 'no-store'
      });
      if (!resp.ok) {
        return;
      }

      const { version } = (await resp.json()) as { version?: string };
      if (!version || version === environment.buildVersion) {
        // A rollback to the running build: nothing is waiting any more.
        this.pendingVersion = null;
        return;
      }
      if (this.inApp) {
        this.zone.run(() => this.promptReload(version));
      } else {
        this.pendingVersion = version;
      }
    } catch {
      // Offline or a deploy in progress - the next check will retry.
    } finally {
      this.checking = false;
    }
  }

  private promptReload(version: string): void {
    if (this.modalRef || this.handedOff) {
      return;
    }

    this.pendingVersion = null;
    this.modalVersion = version;
    this.modalRef = this.modal.create({
      nzTitle: this.transloco.translate('appVersion.title'),
      nzContent: this.transloco.translate('appVersion.content'),
      nzClosable: false,
      nzMaskClosable: false,
      nzKeyboard: false,
      nzCancelText: this.transloco.translate('appVersion.later', {
        minutes: SNOOZE_MS / 60_000
      }),
      nzOnCancel: () => this.snooze(),
      nzOkText: this.transloco.translate('appVersion.reload'),
      nzOnOk: () => {
        this.reload(version);
        // Keep the modal open if the reload was cancelled (unsaved changes prompt).
        return false;
      }
    });
  }

  private snooze(): void {
    this.modalRef = null;
    this.snoozed = true;
    clearTimeout(this.snoozeRef);
    this.zone.runOutsideAngular(() => {
      this.snoozeRef = setTimeout(() => {
        this.snoozed = false;
        this.check();
      }, SNOOZE_MS);
    });
  }

  /**
   * Reloads once normally. If the page comes back still on the old build
   * (a cached index.html), the next attempt adds a cache-busting query so the
   * prompt cannot loop. `target` loads another page of the app instead.
   */
  private reload(version: string, target?: string): void {
    let retried = false;
    try {
      retried = sessionStorage.getItem(RELOAD_TARGET_KEY) === version;
      sessionStorage.setItem(RELOAD_TARGET_KEY, version);
    } catch {
      // Storage unavailable - fall back to a plain reload.
    }

    if (target) {
      this.handedOff = true;
    }
    if (!retried) {
      if (target) {
        window.location.assign(target);
      } else {
        window.location.reload();
      }
      return;
    }

    const url = new URL(target ?? window.location.href, window.location.href);
    url.searchParams.set('_v', version);
    window.location.replace(url.toString());
  }
}
